import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { createUser, updateUser, type User } from '../../../../types'
import { formSubmit } from '../../api/actionApiCall'
import { CheckboxField, SubmitButton, TextField, useFormState } from '../../components/form'

export type UserFormModalProps = {
    show: boolean
    /** Bez uživatele jde o založení nového. */
    user?: User
    onClose: () => void
    onSaved: () => void
}

export function UserFormModal({ show, user, onClose, onSaved }: UserFormModalProps) {
    const isEdit = !!user

    const form = useFormState({
        initialValues: {
            login: user?.login ?? '',
            displayName: user?.displayName ?? '',
            password: '',
            isAdmin: user?.isAdmin ?? false,
            isActive: user?.isActive ?? true,
        },
        onSubmit: async (values) => {
            if (isEdit) {
                const { data, fieldErrors } = await formSubmit(
                    () =>
                        updateUser({
                            path: { userId: user.id },
                            body: {
                                displayName: values.displayName,
                                isAdmin: values.isAdmin,
                                isActive: values.isActive,
                                // Prázdné heslo znamená "neměnit".
                                ...(values.password ? { password: values.password } : {}),
                            },
                        }),
                    { successMessage: 'Uživatel byl upraven.' },
                )
                if (!data) {
                    return { fieldErrors }
                }
            } else {
                const { data, fieldErrors } = await formSubmit(
                    () =>
                        createUser({
                            body: {
                                login: values.login,
                                displayName: values.displayName,
                                password: values.password,
                                isAdmin: values.isAdmin,
                            },
                        }),
                    { successMessage: 'Uživatel byl založen.' },
                )
                if (!data) {
                    return { fieldErrors }
                }
            }

            onSaved()
            onClose()
        },
    })

    return (
        <Modal show={show} onHide={onClose} centered>
            <form onSubmit={form.handleSubmit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5">{isEdit ? `Úprava uživatele ${user.login}` : 'Nový uživatel'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isEdit && (
                        <TextField
                            label="Přihlašovací jméno"
                            value={form.values.login}
                            onChange={(value) => form.setValue('login', value)}
                            error={form.fieldErrors.login}
                            helpText="Písmena bez diakritiky, číslice a znaky . _ -"
                            autoComplete="off"
                            required
                        />
                    )}
                    <TextField
                        label="Jméno"
                        value={form.values.displayName}
                        onChange={(value) => form.setValue('displayName', value)}
                        error={form.fieldErrors.displayName}
                        helpText="Zobrazuje se ostatním ve skupině."
                        required
                    />
                    <TextField
                        label={isEdit ? 'Nové heslo' : 'Heslo'}
                        type="password"
                        value={form.values.password}
                        onChange={(value) => form.setValue('password', value)}
                        error={form.fieldErrors.password}
                        helpText={
                            isEdit
                                ? 'Vyplňte jen při resetu hesla. Uživatel bude odhlášen a při dalším přihlášení si zvolí vlastní heslo.'
                                : 'Dočasné heslo, uživatel si ho při prvním přihlášení změní.'
                        }
                        autoComplete="new-password"
                        required={!isEdit}
                    />
                    <CheckboxField
                        label="Administrátor"
                        checked={form.values.isAdmin}
                        onChange={(checked) => form.setValue('isAdmin', checked)}
                        helpText="Může spravovat uživatele a skupiny."
                    />
                    {isEdit && (
                        <CheckboxField
                            label="Aktivní účet"
                            checked={form.values.isActive}
                            onChange={(checked) => form.setValue('isActive', checked)}
                            helpText="Neaktivní uživatel se nemůže přihlásit, ale zůstává v historii skupin."
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} className="su-tappable">
                        Zrušit
                    </Button>
                    <SubmitButton submitting={form.submitting}>{isEdit ? 'Uložit' : 'Založit'}</SubmitButton>
                </Modal.Footer>
            </form>
        </Modal>
    )
}
